/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/

import { FC, useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    Box,
    Typography,
} from "@mui/material";
import Button from "components/generals/Button";
import DefaultCell from "components/generals/DefaultCell";
import DataTable from "dashboard-template/examples/Tables/DataTable";
import { PrescriptionInterface, HeaderEnum, PrescriptionTypeEnum } from "@malmur-technologies/medicalsystem-shared";
import { useMaterialUIController } from "context/ThemeContext";
import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";
import FormTextEditor from "components/generals/FormTextEditor";

/**
 * Props for PrescriptionHistoryModal component.
 */
interface PrescriptionHistoryModalProps {
    open: boolean;
    onClose: () => void;
    appointmentId: string;
}

/**
 * Modal component to display prescription version history.
 * 
 * Features:
 * - Displays all prescription versions for an appointment
 * - Shows version number, date, type, and reason for change
 * - Allows viewing prescription content in read-only mode
 * - Groups prescriptions by type (Medicine, Lab, Imaging)
 * - Color-coded chips for prescription types
 * 
 * @param {PrescriptionHistoryModalProps} props - Component properties
 * @returns {JSX.Element} Prescription history modal
 */
const PrescriptionHistoryModal: FC<PrescriptionHistoryModalProps> = ({
    open,
    onClose,
    appointmentId,
}) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const [isLoading, , sendRequest] = useHttp();
    const [prescriptions, setPrescriptions] = useState<PrescriptionInterface[]>([]);
    const [selectedPrescription, setSelectedPrescription] = useState<PrescriptionInterface | null>(null);
    const [viewMode, setViewMode] = useState(false);

    useEffect(() => {
        if (open && appointmentId) {
            loadPrescriptionHistory();
        }
    }, [open, appointmentId]);

    const loadPrescriptionHistory = async () => {
        const result = await sendRequest(
            CoreAPI_URLs.PRESCRIPTION_GET_VERSIONS,
            null,
            "GET",
            undefined,
            [{ header: HeaderEnum.RESOURCE_ID, value: appointmentId }]
        );

        if (result?.data) {
            setPrescriptions(result.data);
        }
    };

    const handleViewPrescription = (prescription: PrescriptionInterface) => {
        setSelectedPrescription(prescription);
        setViewMode(true);
    };

    const handleCloseView = () => {
        setSelectedPrescription(null);
        setViewMode(false);
    };

    const getPrescriptionTypeLabel = (type: PrescriptionTypeEnum) => {
        switch (type) {
            case PrescriptionTypeEnum.MEDICINE_PRESCRIPTION:
                return t("progressNoteForm.medication");
            case PrescriptionTypeEnum.LAB_ORDER:
                return t("progressNoteForm.labTest");
            case PrescriptionTypeEnum.IMAGING_ORDER:
                return t("progressNoteForm.imagingTest");
            default:
                return type;
        }
    };

    const getPrescriptionTypeColor = (type: PrescriptionTypeEnum): "primary" | "success" | "info" => {
        switch (type) {
            case PrescriptionTypeEnum.MEDICINE_PRESCRIPTION:
                return "primary";
            case PrescriptionTypeEnum.LAB_ORDER:
                return "success";
            case PrescriptionTypeEnum.IMAGING_ORDER:
                return "info";
            default:
                return "primary";
        }
    };

    const getUITable = (list: PrescriptionInterface[]) => {
        return {
            columns: [
                {
                    Header: t("progressNoteForm.version"),
                    accessor: "version",
                    sortIndicatorType: "numeric",
                    Cell: ({ value }: any) => (
                        <Chip label={`v${value}`} size="small" />
                    ),
                },
                {
                    Header: t("progressNoteForm.type"),
                    accessor: "type",
                    sortIndicatorType: "az",
                    Cell: ({ value }: any) => (
                        <Chip
                            label={getPrescriptionTypeLabel(value)}
                            color={getPrescriptionTypeColor(value)}
                            size="small"
                        />
                    ),
                },
                {
                    Header: t("progressNoteForm.date"),
                    accessor: "createdAt",
                    sortIndicatorType: "date",
                    Cell: ({ value }: any) => (
                        <DefaultCell
                            value={
                                value
                                    ? new Date(value).toLocaleDateString(navigator.language || undefined, {
                                        year: 'numeric',
                                        month: '2-digit',
                                        day: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                    })
                                    : "-"
                            }
                        />
                    ),
                },
                {
                    Header: t("progressNoteForm.reasonForChange"),
                    accessor: "reasonForChange",
                    sortIndicatorType: "az",
                    Cell: ({ value }: any) => <DefaultCell value={value || "-"} />,
                },
                {
                    Header: t("progressNoteForm.actions"),
                    accessor: "id",
                    disableSortBy: true,
                    Cell: ({ value }: any) => {
                        const prescription = list.find((p) => p.id === value);
                        return (
                            <Button
                                name="viewPrescriptionButton"
                                variant="contained"
                                color="info"
                                size="small"
                                onClick={() => {
                                    if (prescription) {
                                        handleViewPrescription(prescription);
                                    }
                                }}
                            >
                                {t("progressNoteForm.view")}
                            </Button>
                        );
                    },
                },
            ],
            rows: list,
        };
    };

    const dataTableData = useMemo(() => getUITable(prescriptions), [prescriptions, t]);

    return (
        <>
            <Dialog
                open={open && !viewMode}
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        minHeight: "70vh",
                        backgroundColor: darkMode ? '#2d3748' : '#ffffff',
                    },
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="h6">
                            {t("progressNoteForm.prescriptionHistory")}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    {prescriptions.length === 0 && !isLoading ? (
                        <Typography variant="body1" textAlign="center" py={4}>
                            {t("progressNoteForm.noPrescriptionHistory")}
                        </Typography>
                    ) : (
                        <DataTable
                            table={dataTableData}
                            entriesPerPage={false}
                            showTotalEntries={true}
                            isSorted={true}
                            noEndBorder={false}
                            isLoading={isLoading}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} color="secondary">
                        {t("progressNoteForm.close")}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={viewMode}
                onClose={handleCloseView}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        minHeight: "60vh",
                    },
                }}
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={2}>
                        <Typography variant="h6">
                            {selectedPrescription &&
                                getPrescriptionTypeLabel(selectedPrescription.type)}
                        </Typography>
                        {selectedPrescription && (
                            <Chip label={`v${selectedPrescription.version}`} size="small" />
                        )}
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedPrescription && (
                        <>
                            <Box mb={2}>
                                <Typography variant="body2" color="textSecondary">
                                    <strong>{t("progressNoteForm.date")}:</strong>{" "}
                                    {selectedPrescription.createdAt
                                        ? new Date(selectedPrescription.createdAt).toLocaleDateString(navigator.language || undefined, {
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                        })
                                        : "-"}
                                </Typography>
                                {selectedPrescription.reasonForChange && (
                                    <Typography variant="body2" color="textSecondary" mt={1}>
                                        <strong>{t("progressNoteForm.reasonForChange")}:</strong>{" "}
                                        {selectedPrescription.reasonForChange}
                                    </Typography>
                                )}
                                {selectedPrescription.notes && (
                                    <Typography variant="body2" color="textSecondary" mt={1}>
                                        <strong>{t("progressNoteForm.notes")}:</strong>{" "}
                                        {selectedPrescription.notes}
                                    </Typography>
                                )}
                            </Box>
                            <FormTextEditor
                                value={selectedPrescription.description}
                                setFieldValue={() => { }}
                                addExternal={false}
                                setAddExternal={() => { }}
                                editorHeight="40rem"
                                wrapperHeight="45rem"
                                readOnly={true}
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseView} color="secondary">
                        {t("progressNoteForm.close")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PrescriptionHistoryModal;
